
import { Component, Vue, Mixins, Prop, Watch } from "vue-property-decorator";
import { Mutation, namespace, Getter, Action } from "vuex-class";
import { get } from "lodash";
import SiteStatus from "@/components/SiteStatus.vue";
import AlarmQueueHeader from "@/components/AlarmQueueHeader.vue";
import AlarmQueueFooter from "@/components/AlarmQueueFooter.vue";
import EnhancedMapAlarms from "@/components/EnhancedMapAlarms.vue";
import PatrolQueue from "@/components/event-queue/PatrolQueue.vue";
import ParkedQueue from "@/components/ParkedQueue.vue";
import ProcessingQueue from "@/components/ProcessingQueue.vue";
import ProcessingError from "@/components/ProcessingError.vue";
import InvitationCheck from "@/components/InvitationCheck.vue";
import HighPriorityCheck from "@/components/HighPriorityCheck.vue";
import ManualPatrol from "@/components/ManualPatrol.vue";
import ManualRaise from "@/components/ManualRaise.vue";
import ErrorWarning from "@/components/ErrorWarning.vue";
import ManualAction from "@/components/manual-actions/ManualAction.vue";
import { UserPermissions, FeaturesList } from "@/store/types";
import QueueInactivityWarning from "@/components/event-queue/InactivityWarning.vue";
import AlarmPointManagerDialog from "@/components/alarm-points/AlarmPointManagerDialog.vue";
import ActivityLog from "@/components/activity-log/ActivityLogPopup.vue";
import EventClose from "@/components/EventClose.vue";

import { retry } from "rxjs/operators";
import { axiosInstance } from "@/axios.instance";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin.ts";
import tree from "../components/Tree.vue";
import ManualActionModal from "@/components/manual-actions/ManualActionmodal.vue";

const SiteMonitor = namespace("siteMonitor");
const Eventqueue = namespace("eventqueue");

@Component({
	components: {
		"site-status": SiteStatus,
		"alarm-queue-header": AlarmQueueHeader,
		"alarm-queue": () => import("../components/AlarmQueue.vue"),
		"alarm-queue-ccure": () => import("../components/AlarmQueueCCure.vue"),
		"alarm-queue-footer": AlarmQueueFooter,
		"enhanced-map-alarms": EnhancedMapAlarms,
		"patrol-queue": PatrolQueue,
		"parked-queue": ParkedQueue,
		"processing-queue": ProcessingQueue,
		"invitation-check": InvitationCheck,
		"high-priority-check": HighPriorityCheck,
		"manual-patrol": ManualPatrol,
		"manual-raise": ManualRaise,
		"error-warning": ErrorWarning,
		"manual-action-modal": ManualActionModal,
		"processing-error": ProcessingError,
		"inactivity-warning": QueueInactivityWarning,
		"alarm-points": AlarmPointManagerDialog,
		"activity-log": ActivityLog,
		"event-close": EventClose
	}
})
export default class EventQueue extends Mixins(RegionalHandlerMixin) {
	public events: any[] = [];
	public error: string = "";
	public activeFilters: any = null;
	public currentFilter: number = -1;
	public updating: boolean = false;

	@Eventqueue.Action fetchUserZone: any;

	@Eventqueue.Getter getCountByType: any;
	@Eventqueue.Getter("getActivityLogShown") activityLogShown!: boolean;
	@Eventqueue.Getter("getBulkHandleShown") bulkHandleShown!: boolean;
	@Eventqueue.Getter("getAllAlarmsSelected") allAlarmsSelected!: boolean;
	@Eventqueue.Getter("getSelectedAlarms") selectedAlarms: number[];
	@Eventqueue.Mutation setLastEventHandled: () => void;
	@Eventqueue.Mutation setAllAlarmsSelected: (allSelected: boolean) => void;
	@Eventqueue.Mutation setAllAlarmsClicked: (clicked: boolean) => void;
	@Eventqueue.Mutation removeSelectedAlarm: (data: number) => void;
	@Eventqueue.Mutation setEndEventsDialogShown: (shown: boolean) => void;

	@SiteMonitor.Mutation setEventCloseShown: any;
	@SiteMonitor.Getter("getEventCloseShown") eventCloseShown: boolean;

	// Flag for toggling the map.
	@Eventqueue.Action setHideMapFlag: any;
	@Eventqueue.Getter("getHideMapFlag") hideMapFlag: boolean;

	@Action fetchRoutingGroups: any;

	@Mutation setShortcuts: any;
	@Mutation setLegendKeys: any;
	@Mutation setRoutingGroupsLegend: any;

	@Getter getPermissions: UserPermissions;
	@Getter getAlarmScreenTimeoutSeconds: number | null;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@SiteMonitor.State(state => state.RequestedRoute) requestedRoute: string;
	@SiteMonitor.State(state => state.LastOperatorMessageVisible) lastOperatorMessageVisible: boolean;
	@SiteMonitor.Mutation setlastOperatorMessageVisible: (isVisibe: boolean) => void;

	@Eventqueue.State(state => state.showIntercomModal) intercomModalIsShown: boolean;

	// Props to allow customization of the component (used by the views alarm queue plugin)
	@Prop({ type: Boolean, default: true }) showMenu: boolean;
	@Prop({ type: Boolean, default: true }) showMap: boolean;
	@Prop({ type: Boolean, default: true }) showHighPriBanner: boolean;
	@Prop({ type: Boolean, default: false }) suppressAudioAlerts: boolean;
	@Prop({ type: Boolean, default: false }) suppressVisualAlerts: boolean;

	@Watch("intercomModalIsShown")
	public intercomModalIsShownChange(value: boolean, oldValue: boolean) {
		// if we not already suppressing the audio alerts (based on the prop)
		if (!this.suppressAudioAlerts) {
			if (value) {
				console.log("Muting alarm notification sound while using intercom");
			} else {
				console.log("Resuming alarm notification sound");
			}

			this.suppressAudioAlertsNotification = value;
		}
	}

	private suppressAudioAlertsNotification: boolean = false;

	private okLastOperatorMessage(): void {
		this.setlastOperatorMessageVisible(false);
		this.$router.push({ path: this.requestedRoute });
	}

	private cancelLastOperatorMessage(): void {
		this.setlastOperatorMessageVisible(null);
	}

	public created() {
		this.fetchUserZone();
		this.fetchRoutingGroups();
		this.setLastEventHandled();

		// set default value based on prop
		this.suppressAudioAlertsNotification = this.suppressAudioAlerts;
	}

	public mounted() {
		this.setShortcuts([
			{ keys: "Ctrl+R", action: "Manual Raise" },
			{ keys: "Ctrl+M", action: "Manual Tour" },
			{
				keys: "Ctrl+P",
				action: "Process Top Alarm",
				featureFlag: "Alarms.SiteMonitor"
			},
			{ keys: "Ctrl+Q", action: "Quick Control", featureFlag: "Alarms.QuickControl" },
			{ keys: "Ctrl+[1-0]", action: "Change between first 10 filters" }
		]);
		this.setLegendKeys([
			{
				icon: "fa-bell text-danger",
				details: "Alarms are awaiting a response"
			},
			//{ icon: 'circle smaller tier2', details: 'Routed Group Alarm - Colour depends on the routing group' }, //@TODO: Maybe add dynamic routing group keys by pulling from DB
			{ icon: "fa-hourglass-end", details: "Previously parked event" }
		]);
		this.setRoutingGroupsLegend(true);

		// Prevent the user from closing window if warnLastOperator is enabled
		if (this.isWarnLastOperatorEnabled) {
			window.addEventListener("beforeunload", function(e) {
				// Cancel the event
				e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
				// Chrome requires returnValue to be set
				e.returnValue = "";
			});
		}
	}

	public get patrolEventCount() {
		return this.getCountByType("patrols");
	}

	public get parkedEventCount() {
		return this.getCountByType("parked");
	}

	public get processingEventCount() {
		return this.getCountByType("inProcess");
	}

	public get alarmQueueType() {
		// We use lodash here as native ES does not have null propagation
		return get(this.featuresList, ["Integration", "CCure", "UI"]) ? "alarm-queue-ccure" : "alarm-queue";
	}

	public get isHighPriorityBannerEnabled() {
		return (
			this.showHighPriBanner &&
			get(this.featuresList, ["Alarms", "HighPriorityBanner"]) &&
			this.getPermissions.canInteractHighPriorityBanner
		);
	}

	public get isActivityLogEnabled() {
		return get(this.featuresList, ["ActivityLog"]);
	}

	// Feature flag check for toggle maps.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleEventQueue"]);
	}

	// public get / set that wrapper the store to avoid warnings binding to the checkbox
	// get
	public set allSelected(allAlarmsSelected: boolean) {
		this.setAllAlarmsSelected(allAlarmsSelected);
	}

	// set
	public get allSelected(): boolean {
		return this.allAlarmsSelected;
	}

	// Controls the state when 'SelectAll' toggled
	public allAlarmsToggled() {
		// Set the property to indicate the global 'Select All' toggle has been clicked
		this.setAllAlarmsClicked(true);

		// calls the store action, inverting the value for allAlarmsSelected currently stored in the store state
		this.setAllAlarmsSelected(!this.allAlarmsSelected);
	}

	public endSelectedAlarms() {
		this.setEventCloseShown(true);
	}

	// Feature flag check for map component
	private get isMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Map"]);
	}

	private get isWarnLastOperatorEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "WarnLastOperator"]);
	}
}
